import { useEffect, useState } from "react";
import { Loader2, Monitor, RotateCcw, Save, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { api, errText } from "../../lib/api";
import { VerifyPromo } from "../../components/home/VerifyPromo";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Slider } from "../../components/ui/slider";
import { Button } from "../../components/ui/button";
import { Field, ImagePick } from "./InfoBoxEditor";

export const Card = ({ title, children }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-5"><p className="font-bold text-[#12234A] mb-4">{title}</p>{children}</div>
);

export const Pair = ({ s, set, k, label, max, area, pre = "promo" }) => {
  const C = area ? Textarea : Input;
  return (
    <div className="grid md:grid-cols-2 gap-4">
      <Field label={`${label} (English)`}><C rows={area ? 3 : undefined} value={s[`${k}_en`]} onChange={(e) => set(`${k}_en`, e.target.value)} maxLength={max} data-testid={`${pre}-${k}-en`} /></Field>
      <Field label={`${label} (French)`}><C rows={area ? 3 : undefined} value={s[`${k}_fr`]} onChange={(e) => set(`${k}_fr`, e.target.value)} maxLength={max} data-testid={`${pre}-${k}-fr`} /></Field>
    </div>
  );
};

export const Color = ({ s, set, k, label, pre = "promo" }) => (
  <label className="flex items-center gap-2 text-sm text-[#12234A]">
    <input type="color" value={s[k]} onChange={(e) => set(k, e.target.value.toUpperCase())} className="h-9 w-11 rounded-lg border border-slate-200 cursor-pointer bg-white p-0.5" data-testid={`${pre}-${k}`} />
    <span><span className="font-semibold">{label}</span><span className="block text-[11px] text-slate-400 font-mono">{s[k]}</span></span>
  </label>
);

export const Range = ({ s, set, k, label, min, max, unit = "px", testid, step, pre = "promo" }) => (
  <div>
    <div className="flex justify-between text-xs font-semibold text-slate-500 mb-2"><span>{label}</span><span className="text-[#12234A]">{s[k]}{unit}</span></div>
    <Slider value={[s[k]]} min={min} max={max} step={step || (unit === "px" ? 10 : 5)} onValueChange={([v]) => set(k, v)} data-testid={testid || `${pre}-${k}`} />
  </div>
);

export function VerifyPromoSettings() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [lang, setLang] = useState("en");
  const [mode, setMode] = useState("desktop");
  const [showPrev, setShowPrev] = useState(true);
  useEffect(() => { api.get("/admin/site/verify-promo-box").then(({ data }) => setS(data)).catch((e) => toast.error(errText(e))); }, []);
  if (!s) return <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-slate-300" /></div>;
  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));
  const save = async () => {
    setSaving(true);
    try { const { data } = await api.put("/admin/site/verify-promo-box", s); setS((p) => ({ ...p, ...data })); toast.success("Banner saved"); }
    catch (e) { toast.error(errText(e)); } finally { setSaving(false); }
  };
  const reset = () => { const { defaults } = s; setS({ ...defaults, defaults }); toast("Original design loaded — press Save to keep it"); };
  const tab = (on) => `h-8 px-3 rounded-lg text-xs font-bold inline-flex items-center gap-1.5 ${on ? "bg-[#12234A] text-white" : "bg-white text-slate-500 border"}`;

  return (
    <div className="space-y-5" data-testid="verify-promo-settings">
      <div className="rounded-2xl border border-slate-200 bg-white p-5 flex flex-wrap items-center gap-4">
        <label className="flex items-center gap-3"><Switch checked={s.enabled} onCheckedChange={(v) => set("enabled", v)} data-testid="promo-enabled" />
          <span className="text-sm font-semibold text-[#12234A]">Show the "Get verified" banner on the homepage</span></label>
        <div className="ml-auto flex gap-2">
          <Button variant="outline" onClick={reset} className="rounded-xl gap-2" data-testid="promo-reset"><RotateCcw size={15} /> Original design</Button>
          <Button onClick={save} disabled={saving} className="rounded-xl bg-[#F5551E] hover:bg-[#E04812] text-white gap-2" data-testid="promo-save">
            {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save
          </Button>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 sticky top-2 z-10" data-testid="promo-preview">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <p className="text-xs font-bold uppercase tracking-widest text-slate-400">Live preview {!s.enabled && "· hidden on homepage"}</p>
          <div className="flex gap-1">
            <button type="button" onClick={() => setMode("desktop")} className={tab(mode === "desktop")} data-testid="promo-preview-desktop"><Monitor size={13} /> Computer</button>
            <button type="button" onClick={() => setMode("mobile")} className={tab(mode === "mobile")} data-testid="promo-preview-mobile"><Smartphone size={13} /> Phone</button>
            <span className="w-2" />
            {["en", "fr"].map((l) => <button key={l} type="button" onClick={() => setLang(l)} className={tab(lang === l)} data-testid={`promo-preview-${l}`}>{l.toUpperCase()}</button>)}
            <span className="w-2" />
            <button type="button" onClick={() => setShowPrev((v) => !v)} className={tab(false)} data-testid="promo-preview-toggle">{showPrev ? "Hide" : "Show"}</button>
          </div>
        </div>
        <div className={`${mode === "mobile" ? "max-w-[380px] mx-auto" : ""} max-h-[38vh] overflow-auto ${showPrev ? "" : "hidden"}`}>
          <VerifyPromo s={s} lang={lang} mode={mode} />
        </div>
      </div>

      <Card title="Wording">
        <div className="space-y-4">
          <Pair s={s} set={set} k="badge" label="Small badge (leave empty to hide)" max={40} />
          <Pair s={s} set={set} k="title" label="Heading" max={90} />
          <Pair s={s} set={set} k="title_b" label="Test heading B — optional, shown to half of visitors" max={90} />
          <Pair s={s} set={set} k="body" label="Text" max={400} area />
        </div>
      </Card>

      <Card title="Buttons">
        <div className="space-y-4">
          <Pair s={s} set={set} k="btn1" label="Main button text" max={30} />
          <Field label="Main button link (a page like /get-verified, or https://…)"><Input value={s.btn1_link} onChange={(e) => set("btn1_link", e.target.value)} data-testid="promo-btn1-link" /></Field>
          <Pair s={s} set={set} k="btn2" label="Second button text — optional" max={30} />
          <Field label="Second button link"><Input value={s.btn2_link} onChange={(e) => set("btn2_link", e.target.value)} placeholder="/litter-journey" data-testid="promo-btn2-link" /></Field>
        </div>
      </Card>

      <Card title="Look & size">
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-5">
            <Range s={s} set={set} k="height_desktop" label="Height on computers" min={140} max={600} />
            <Range s={s} set={set} k="height_mobile" label="Height on phones" min={200} max={800} />
            <p className="text-[11px] text-slate-400 -mt-2">The banner grows if the wording needs more room.</p>
            <div>
              <p className="text-xs font-semibold text-slate-500 mb-2">Text position</p>
              <div className="flex gap-1">{[["left", "Left"], ["center", "Centred"]].map(([v, l]) => (
                <button key={v} type="button" onClick={() => set("align", v)} className={tab(s.align === v)} data-testid={`promo-align-${v}`}>{l}</button>
              ))}</div>
            </div>
            <label className="flex items-center gap-3"><Switch checked={s.glow} onCheckedChange={(v) => set("glow", v)} data-testid="promo-glow" /><span className="text-sm text-[#12234A]">Soft glow in the corner</span></label>
          </div>
          <div className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <Color s={s} set={set} k="bg_color" label="Background" />
              <Color s={s} set={set} k="text_color" label="Text" />
              <Color s={s} set={set} k="accent_color" label="Badge icon & glow" />
              <Color s={s} set={set} k="btn1_bg" label="Main button" />
              <Color s={s} set={set} k="btn1_text" label="Main button text" />
            </div>
            <div>
              <p className="text-xs font-semibold text-slate-500 mb-2">Background image — optional</p>
              <ImagePick label="background" value={s.bg_image} onChange={(v) => set("bg_image", v)} testid="promo-bg" />
            </div>
            {s.bg_image && <Range s={s} set={set} k="overlay" label="Colour tint over the image" min={0} max={90} unit="%" testid="promo-overlay" />}
          </div>
        </div>
      </Card>
    </div>
  );
}
